import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  FaSearch, FaFilePdf, FaEye, FaTrash, FaEdit,
  FaArrowLeft, FaArrowRight, FaUserSlash
} from "react-icons/fa";
import { exportarProfessores } from "../utils/exportarProfessores";
import ProfessorModal from "../components/Professores/ProfessorModal";
import EditarProfessorModal from "../components/Professores/EditarProfessorModal";
import ResumoProfessores from "../components/Professores/ResumoProfessores";
import { useNotification } from "../hooks/useNotification";
import deleteSound from "../assets/sounds/delete.mp3";
import activateSound from "../assets/sounds/activate.mp3";
import "./ListaProfessores.css";

const opcoesDisciplinas = [
  "Todas", "Matemática", "Português", "Física", "Química", "Ed.Visual", "Ed.Física",
  "Biologia", "Agro-pecuária", "História", "Geografia", "Filosofia", "Francês", "Inglês"
];

const opcoesClasses = [
  "Todas", "1ª Classe", "2ª Classe", "3ª Classe", "4ª Classe", "5ª Classe",
  "6ª Classe", "7ª Classe", "8ª Classe", "9ª Classe", "10ª Classe", "11ª Classe", "12ª Classe"
];

const ListaProfessores = () => {
  const [professores, setProfessores] = useState([]);
  const [filtroNome, setFiltroNome] = useState("");
  const [filtroDisciplinas, setFiltroDisciplinas] = useState([]);
  const [filtroClasses, setFiltroClasses] = useState([]);
  const [professorSelecionado, setProfessorSelecionado] = useState(null);
  const [editarProfessor, setEditarProfessor] = useState(null);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const professoresPorPagina = 10;
  const audioRef = useRef(null);
  const { notifySuccess, notifyError } = useNotification();

  useEffect(() => {
    fetchProfessores();
  }, []);

  const playSound = (src) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    audioRef.current = new Audio(src);
    audioRef.current.play();
  };

  const fetchProfessores = () => {
    fetch("/api/professores")
      .then((response) => response.json())
      .then((data) => {
        console.log("Dados dos professores recebidos:", data);
        setProfessores(data);
      })
      .catch((error) => {
        console.error("Erro ao buscar professores:", error);
        notifyError("Erro ao buscar professores.");
      });
  };

  const filtrarProfessores = (lista = []) => {
    if (!Array.isArray(lista)) return [];
    return lista.filter((prof) => {
      const nomeOK =
        filtroNome === "" ||
        prof.Nome.toLowerCase().includes(filtroNome.toLowerCase());

      const disciplinasOK =
        filtroDisciplinas.length === 0 ||
        filtroDisciplinas.includes("Todas") ||
        (prof.Disciplinas &&
          filtroDisciplinas.every((fd) => prof.Disciplinas.includes(fd)));

      const classesOK =
        filtroClasses.length === 0 ||
        filtroClasses.includes("Todas") ||
        (prof.Classes &&
          filtroClasses.every((fc) => prof.Classes.includes(fc)));

      return nomeOK && disciplinasOK && classesOK;
    });
  };

  const professoresFiltrados = useMemo(() => filtrarProfessores(professores), [
    professores, filtroNome, filtroDisciplinas, filtroClasses
  ]);

  const totalPaginas = Math.ceil(professoresFiltrados.length / professoresPorPagina);
  const professoresPaginados = professoresFiltrados.slice(
    (paginaAtual - 1) * professoresPorPagina,
    paginaAtual * professoresPorPagina
  );

  const handleExcluirProfessor = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este professor?")) return;
    try {
      const response = await fetch(`/api/professores/${id}`, {
        method: "DELETE"
      });
      if (!response.ok) {
        const resText = await response.text();
        throw new Error(resText || "Erro ao excluir professor.");
      }
      playSound(deleteSound);
      notifySuccess("Professor excluído com sucesso.");
      fetchProfessores();
    } catch (error) {
      console.error("Erro ao excluir professor:", error);
      notifyError("Erro ao excluir professor.");
    }
  };

  const handleDesativar = async (id) => {
    try {
      const response = await fetch(`/api/professores/${id}/desativar`, {
        method: "PATCH"
      });
      if (!response.ok) {
        const resText = await response.text();
        throw new Error(resText || "Erro ao desativar professor.");
      }
      playSound(activateSound);
      notifySuccess("Professor desativado com sucesso.");
      fetchProfessores();
    } catch (err) {
      console.error("Erro ao desativar professor:", err);
      notifyError("Erro ao desativar professor.");
    }
  };

  return (
    <div className="lista-professores">
      <h2>Lista de Professores(as) no Sistema</h2>
      <ResumoProfessores professores={professoresFiltrados} />

      <div className="filtros">
        <div className="filtro-nome">
          <FaSearch />
          <input
            type="text"
            placeholder="Buscar por nome"
            value={filtroNome}
            onChange={(e) => setFiltroNome(e.target.value)}
          />
        </div>

        <div className="filtro-disciplinas">
          <label>Disciplinas:</label>
          <select
            multiple
            size={5}
            value={filtroDisciplinas}
            onChange={(e) =>
              setFiltroDisciplinas(Array.from(e.target.selectedOptions, (o) => o.value))
            }
          >
            {opcoesDisciplinas.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>

        <div className="filtro-classes">
          <label>Classes:</label>
          <select
            multiple
            size={5}
            value={filtroClasses}
            onChange={(e) =>
              setFiltroClasses(Array.from(e.target.selectedOptions, (o) => o.value))
            }
          >
            {opcoesClasses.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div className="botoes-filtros">
          <button onClick={() => exportarProfessores(professoresFiltrados, "pdf")}>
            <FaFilePdf /> Exportar PDF
          </button>
          <button onClick={() => {
            setFiltroNome("");
            setFiltroDisciplinas([]);
            setFiltroClasses([]);
          }}>
            Limpar Filtros
          </button>
        </div>
      </div>

      <div className="lista-container">
        {professoresPaginados.length > 0 ? (
          professoresPaginados.map((prof) => (
            <div key={prof.id} className="professor-card">
              <span onClick={() => setProfessorSelecionado(prof)}>
                <FaEye /> {prof.Nome}
              </span>
              <div className="acoes">
                <button onClick={() => setEditarProfessor(prof)}><FaEdit /> Editar</button>
                <button onClick={() => handleExcluirProfessor(prof.id)}><FaTrash color="red" /></button>
                <button onClick={() => handleDesativar(prof.id)}><FaUserSlash /> Desativar</button>
              </div>
            </div>
          ))
        ) : (
          <p>Nenhum professor encontrado.</p>
        )}
      </div>

      <div className="paginacao">
        {paginaAtual > 1 && <FaArrowLeft onClick={() => setPaginaAtual(paginaAtual - 1)} />}
        <span>{paginaAtual} de {totalPaginas}</span>
        {paginaAtual < totalPaginas && <FaArrowRight onClick={() => setPaginaAtual(paginaAtual + 1)} />}
      </div>

      {professorSelecionado && (
        <ProfessorModal
        key={professorSelecionado.id} // ← Isso garante reconciliação correta
          professor={professorSelecionado}
          onClose={() => setProfessorSelecionado(null)}
        />
      )}

      {editarProfessor && (
        <EditarProfessorModal
        key={editarProfessor.id} // ← Isso garante que o modal será reconstruído corretamente
          professor={editarProfessor}
          onClose={() => setEditarProfessor(null)}
          onSave={() => {
            fetchProfessores();
            setEditarProfessor(null);
            notifySuccess("Professor salvo com sucesso.");
          }}
        />
      )}
    </div>
  );
};

export default ListaProfessores;
